/**
 * §7.2 操作員的落點：`/work` **我的工作台**。
 * 「今天我的工單、可以接續的作業、待完成的繳庫。**一頁就能開工**」
 *
 * mockup 的首頁（同一個儀表板給所有人看 + 26 步 demo 腳本）取消（§7.2），
 * demo 腳本移到 /docs。
 */

import { useQuery } from '../api/hooks.js'
import { useAuth } from '../auth/AuthContext.js'
import { Alert, Button, Card, Empty, Mono, Pill, Table, Td } from '../components/ui.js'
import { useConfig, useDict } from '../config/ConfigContext.js'
import { count, tsShort } from '../../../shared/lib/format.js'
import { Link } from '../router.js'

interface WorkOrderRow {
  work_order_id: string
  station_id: string
  resource_id: string
  planned_count: number
  operator_id: string
  opened_at: string
  status: 'open' | 'closed'
  issue_lines: string
  receipt_lines: string
  has_missing_weight: boolean
}

export function WorkBench() {
  const { me, can } = useAuth()
  const dict = useDict()
  const { config } = useConfig()
  const { data, error, loading, reload } = useQuery<{ items: WorkOrderRow[] }>(
    '/api/work-orders?status=open&limit=100',
  )

  const all = data?.items ?? []
  // §7.3 綁定站台的人只關心自己那一站
  const station = me?.user.defaultStationId ?? null
  const mine = station ? all.filter((w) => w.station_id === station) : all
  const others = station ? all.filter((w) => w.station_id !== station) : []

  const enabledStations = (config?.stations ?? []).filter((s) => s.enabled)

  return (
    <div className="space-y-4">
      <Card
        title="我的工作台"
        subtitle={station ? `${dict.station(station)}（您的預設站台）` : '所有站台'}
        right={<Button size="sm" onClick={reload} disabled={loading}>重新整理</Button>}
      >
        {error && <Alert tone="danger" title="讀不到工單">{error.message}</Alert>}
        {loading && <p className="text-sm text-ink-500">載入中…</p>}

        {!loading && mine.length === 0 && (
          <div className="space-y-3">
            <Empty>目前沒有進行中的工單。</Empty>
            {can('workorder.open') && (
              <div className="flex flex-wrap gap-2 justify-center">
                <Link to="/issue">
                  <Button variant="primary" size="lg">開工單並領料</Button>
                </Link>
                {can('receiving.register') && (
                  <Link to="/receiving"><Button size="lg">進庫（交接）</Button></Link>
                )}
              </div>
            )}
          </div>
        )}

        {mine.length > 0 && (
          <Table head={['工單', '站台', '設備', '領料', '繳庫', '開立', '']}>
            {mine.map((w) => (
              <tr key={w.work_order_id} className="hover:bg-ink-50">
                <Td><Mono>{w.work_order_id}</Mono></Td>
                <Td>{dict.station(w.station_id)}</Td>
                <Td className="text-xs">{dict.resource(w.resource_id)}</Td>
                <Td className="tabular-nums text-xs">{w.issue_lines}</Td>
                <Td className="tabular-nums text-xs">
                  {w.receipt_lines}
                  {w.has_missing_weight && <Pill tone="amber">缺重量</Pill>}
                </Td>
                <Td className="text-xs">{tsShort(w.opened_at)}</Td>
                <Td>
                  <div className="flex gap-1.5">
                    {can('receipt.post') && (
                      <Link to={`/station/${w.station_id}`}>
                        <Button size="sm" variant="primary">繳庫</Button>
                      </Link>
                    )}
                    {can('balance.read') && (
                      <Link to={`/balance/${w.work_order_id}`}>
                        <Button size="sm">結算</Button>
                      </Link>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {/* §7.3：導覽上不出現其他站台，但工作台上讓人知道別站有工單並非壞事 */}
      {others.length > 0 && (
        <Card title="其他站台的工單" subtitle={`${count(others.length)} 張，不是您的預設站台`}>
          <Table head={['工單', '站台', '設備', '開立']} dense>
            {others.map((w) => (
              <tr key={w.work_order_id} className="text-ink-500">
                <Td><Mono>{w.work_order_id}</Mono></Td>
                <Td>{dict.station(w.station_id)}</Td>
                <Td className="text-xs">{dict.resource(w.resource_id)}</Td>
                <Td className="text-xs">{tsShort(w.opened_at)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      )}

      {!station && enabledStations.length > 0 && can('receipt.post') && (
        <Card title="站台作業" subtitle="直接前往某一站">
          <div className="flex flex-wrap gap-2">
            {enabledStations.map((s) => (
              <Link key={s.stationId} to={`/station/${s.stationId}`}>
                <Button size="lg">{s.label}</Button>
              </Link>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
